import { HeadingH4, Text_16_24 } from '@dreamstack/feature-components'
import {
  useFormatDateShort,
  useFormatToDecimal,
  useTranslation,
} from '@dreamstack/i18n'
import { Download } from '@dreamstack/icons'
import type { BlockAnalystRatingFragment } from '@dreamstack/investors-graphql'
import { SimpleDefinitionList } from '@dreamstack/simple-components'
import Link from 'next/link'
import type { FunctionComponent } from 'react'
import tw, { styled } from 'twin.macro'

const FramedByBorders = styled.div<{ hideBottomBorder?: boolean }>(
  ({ hideBottomBorder }) => [
    tw`flex border-t-2 py-px-16 my-px-24`,
    !hideBottomBorder && tw` border-b-2`,
  ]
)

const StyledDownload = tw(
  Download
)`text-accentroAqua-full font-semibold height[24px] width[24px] md:(height[32px] width[32px])`

const StyledHeadingH4 = styled(HeadingH4)(() => [
  tw`bg-accentroAqua-100 text-accentroAqua-full flex justify-center items-center width[145px] h-px-48`,
])

const BlockTopLine = tw.div`w-px-196 border-t-2 border-accentroGray-600 pb-px-24`

export const AnalystRating: FunctionComponent<React.PropsWithChildren<{
  block: BlockAnalystRatingFragment
}>> = ({ block }) => {
  const {
    download: file,
    targetPrice,
    name,
    company,
    recommendation,
    noteText,
    coverageCurrentlySuspended,
  } = block

  const t = useTranslation()
  const targetLabel = t('accentro:analystRatings.target')
  const dateLabel = t('accentro:analystRatings.date')
  const downloadLabel = t('accentro:analystRatings.download')
  const coverageLabel = !!noteText
    ? noteText
    : t('accentro:analystRatings.coverageSuspended')
  const showInformation =
    coverageCurrentlySuspended !== undefined && !coverageCurrentlySuspended

  const formatDecimal = useFormatToDecimal()
  const formattedTargetPrice =
    typeof targetPrice === 'number' ? formatDecimal(targetPrice) : '-'

  const formatDate = useFormatDateShort()
  const formattedDate = formatDate(block.date)

  return (
    <>
      <div>
        <BlockTopLine />
        <HeadingH4 tw="ml:text-px-24 pb-px-8">{name}</HeadingH4>
        <div>{company}</div>
        {showInformation ? (
          <>
            <FramedByBorders>
              <div tw="space-y-px-20 lg:(flex space-y-0)">
                <SimpleDefinitionList
                  dt={
                    <Text_16_24 tw="mt-px-4 font-normal">
                      {targetLabel}
                    </Text_16_24>
                  }
                  dd={
                    <Text_16_24 tw="font-semibold">
                      {formattedTargetPrice}
                      <span tw="pl-px-4">EUR</span>
                    </Text_16_24>
                  }
                />
                <SimpleDefinitionList
                  tw="lg:px-px-40"
                  dt={
                    <Text_16_24 tw="mt-px-4 font-normal">
                      {dateLabel}
                    </Text_16_24>
                  }
                  dd={
                    <Text_16_24 tw="font-semibold whitespace-nowrap">
                      {formattedDate}
                    </Text_16_24>
                  }
                />
              </div>
              <div tw=" flex w-full justify-end items-end lg:items-center">
                <StyledHeadingH4>{recommendation}</StyledHeadingH4>
              </div>
            </FramedByBorders>

            {/* TODO: Design this height, maybe depending on Viewport? */}
            <div tw="h-px-64">
              {file?.url ? (
                <Link
                  href={file.url}
                  passHref
                  tw="text-accentroAqua-full flex cursor-pointer w-max items-center"
                  target="_blank"
                >
                  <StyledDownload />
                  <HeadingH4 tw="pl-px-12">{downloadLabel}</HeadingH4>
                </Link>
              ) : (
                '-------'
              )}
            </div>
          </>
        ) : (
          <FramedByBorders hideBottomBorder>{coverageLabel}</FramedByBorders>
        )}
      </div>
    </>
  )
}
